import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ClockIcon, FamilyIcon, GPSIcon, HealthAiIcon, ModelIcon, UsersIcon, VideoIcon, WarningIcon } from "../icons/icons";

// ─── Floating Particle ───────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color?: string }> = ({ x, delay, color = "#FF6701" }) => (
    <motion.div
        className="absolute w-1 h-1 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "10%", backgroundColor: color, opacity: 0.6 }}
        animate={{ y: [0, -45, 0], opacity: [0, 0.7, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Feature Chip Component ──────────────────────────────────────────────────
interface FeatureChipProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    index: number;
}

const FeatureChip: React.FC<FeatureChipProps> = ({ icon, title, description, index }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 3 }, (_, i) => ({ id: Date.now() + i, x: 20 + Math.random() * 60 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2100);
    };

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    return (
        <motion.div
            className="relative flex items-center gap-3 cursor-pointer overflow-hidden rounded-2xl p-3"
            style={{
                background: hovered ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,247,237,0.85) 100%)" : "transparent",
                border: `1px solid ${hovered ? "rgba(255,103,1,0.25)" : "transparent"}`,
                backdropFilter: hovered ? "blur(12px)" : "none",
                WebkitBackdropFilter: hovered ? "blur(12px)" : "none",
                boxShadow: hovered ? "0 8px 24px -6px rgba(255,103,1,0.18)" : "none",
                transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
            whileHover={{ scale: 1.04, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onHoverStart={() => {
                setHovered(true);
                spawnParticles();
            }}
            onHoverEnd={() => setHovered(false)}
        >
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} x={p.x} delay={0} color="#FF6701" />
                ))}
            </div>

            {/* Top shimmer divider */}
            <motion.div
                className="absolute top-0 left-4 right-4 h-[1.5px] rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, #FF6701, transparent)" }}
                animate={{ opacity: hovered ? 0.7 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Icon */}
            <motion.div
                className="flex-shrink-0 relative text-[#FF6701]"
                animate={{ rotate: hovered ? [0, -8, 8, 0] : 0, scale: hovered ? 1.1 : 1 }}
                transition={{ duration: 0.4 }}
            >
                {hovered && (
                    <motion.div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ border: "1.5px solid rgba(255,103,1,0.35)" }}
                        initial={{ scale: 1, opacity: 0.5 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 0.9, repeat: Infinity }}
                    />
                )}
                {icon}
            </motion.div>

            {/* Text */}
            <div className="flex flex-col">
                <motion.h3
                    className="font-semibold mb-0.5 text-sm transition-colors duration-300"
                    animate={{ color: hovered ? "#FF6701" : "#1e293b" }}
                >
                    {title}
                </motion.h3>
                <motion.p
                    className="text-xs leading-relaxed transition-colors duration-300"
                    animate={{ color: hovered ? "#78716c" : "#5D697F" }}
                >
                    {description}
                </motion.p>
            </div>
        </motion.div>
    );
};

const trackersData = [
    {
        background: "../../media/trackerbackground3.webp",
        shadow: "../../media/trackershadow3.webp",
        ring: "../../media/tracker3.webp",
        shadowStyle: { x: -70, y: -150, scale: 3 },
        ringStyle: { x: -68, y: -172, scale: 2 },
        ringStyleMobile: { x: -60, y: -172, scale: 2 },
        ringWidth: "70%",
    },
    {
        background: "../../media/trackerbackground2.webp",
        shadow: "../../media/trackershadow2.webp",
        ring: "../../media/tracker2.webp",
        shadowStyle: { x: -70, y: 125, scale: 3.25 },
        ringStyle: { x: -68, y: 70, scale: 2 },
        ringStyleMobile: { x: -60, y: 70, scale: 2 },
        ringWidth: "70%",
    },
    {
        background: "../../media/trackerbackground1.webp",
        shadow: "../../media/trackershadow1.webp",
        ring: "../../media/tracker1.webp",
        shadowStyle: { x: -71, y: -150, scale: 3.25 },
        ringStyle: { x: -68, y: -172, scale: 2 },
        ringStyleMobile: { x: -60, y: -172, scale: 2 },
        ringWidth: "70%",
    },
];

interface HeroSectionProps {
    onCompareClick: () => void;
}

const TrackerHeroSection: React.FC<HeroSectionProps> = ({ onCompareClick }) => {
    const { t, i18n } = useTranslation("TrackerHero");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handleSmoothScroll = () => {
        const targetElement = document.getElementById("comparison-section") || document.querySelector('[data-section="comparison"]') || document.querySelector(".comparison-section");

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        } else {
            window.scrollTo({
                top: window.innerHeight,
                behavior: "smooth",
            });
        }

        onCompareClick();
    };

    const features = [
        {
            title: t("features.gpsTracking.title"),
            description: t("features.gpsTracking.description"),
            icon: <GPSIcon />,
        },
        {
            title: t("features.familySharing.title"),
            description: t("features.familySharing.description"),
            icon: <FamilyIcon />,
        },
        {
            title: t("features.geofenceAlert.title"),
            description: t("features.geofenceAlert.description"),
            icon: <WarningIcon />,
        },
    ];

    const buttons = [
        {
            text: t("buttons.compare"),
            icon: <ModelIcon />,
            className: "bg-[#FF6701] text-white",
            shadow: "0 10px 25px -5px rgba(249, 115, 22, 0.4)",
            onClick: handleSmoothScroll,
        },
        {
            text: t("buttons.recommended"),
            icon: <VideoIcon />,
            className: "bg-white text-[#293048]",
            shadow: "0 10px 25px -5px rgba(107, 114, 128, 0.2)",
            onClick: () => {},
        },
    ];

    const getTransformStyle = (styleData: { x: number; y: number; scale: number }) => {
        return `translate(${styleData.x}%, ${styleData.y}%) scale(${styleData.scale})`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative pt-0 sm:pt-16 overflow-x-hidden"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Main Container */}
            <div className="container px-4 sm:px-10 pb-4 lg:pb-6 pt-4 sm:pt-20 flex flex-col md:flex-row-reverse items-center justify-between gap-5 sm:gap-0">
                {/* Product Images */}
                <div className="w-full md:w-1/2 mb-4 md:mb-0 relative sm:px-2 flex justify-center items-center">
                    <img src="media/trackerHero.webp" alt={t("images.smartTracker")} className="w-full max-w-xs sm:max-w-xl h-auto object-contain drop-shadow-xl" />
                </div>

                {/* Text Content */}
                <div className="w-full md:w-1/2 text-center sm:text-start z-10">
                    <div className="max-w-lg mx-auto sm:mx-0">
                        {/* Badge */}
                        <div className="border border-[#222323] rounded-full w-fit py-1 px-3 mx-auto sm:mx-0 mb-3 sm:mb-8">
                            <p className="text-[#293048] font-medium text-xs sm:text-sm">{t("badge")}</p>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-black">{t("title")}</h1>
                        <h2 className="text-xl sm:text-4xl mb-3 sm:mb-6 font-medium">
                            {t("subtitle.part1")} <span className="bg-gradient-to-r from-[#A44302] to-[#F86300] bg-clip-text text-transparent">{t("subtitle.highlight")}</span>
                        </h2>
                        <p className="text-[#293048] mb-5 sm:mb-8 text-sm sm:text-lg leading-relaxed">{t("description")}</p>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-3 sm:gap-4">
                            {buttons.map((btn, idx) => (
                                <motion.button
                                    key={idx}
                                    whileHover={{
                                        scale: 1.03,
                                        boxShadow: btn.shadow,
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={btn.onClick}
                                    className={`w-full sm:w-fit text-sm sm:text-base flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 ${btn.className}`}
                                >
                                    {btn.icon}
                                    <span>{btn.text}</span>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Footer */}
            <div className="container mx-auto px-4 sm:px-10">
                <motion.div
                    className="flex flex-col md:flex-row-reverse justify-between sm:items-center gap-6 sm:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                >
                    {/* Feature Items */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full justify-around">
                        {features.map((feature, index) => (
                            <FeatureChip
                                key={index}
                                index={index}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                            />
                        ))}
                    </div>

                    {/* Partnership Badge */}
                    <motion.div
                        className="bg-orange-50/60 py-3.5 px-3 lg:px-6 rounded-2xl w-full md:w-auto border border-orange-100/60 cursor-pointer overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.7 }}
                        whileHover={{
                            scale: 1.03,
                            boxShadow: "0 8px 24px -6px rgba(255,103,1,0.18)",
                            borderColor: "rgba(255,103,1,0.3)",
                        }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <div className="flex items-center justify-center gap-3">
                            <img src="../../../media/HamrahAvval.svg" alt={t("partnership.logoAlt")} className="h-4" />
                            <p className="text-[#5D697F] text-sm border-s border-gray-300 ps-3">{t("partnership.text")}</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default TrackerHeroSection;
